import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { DatabaseZapIcon } from 'lucide-react'
import { useMemo } from 'react'

import { Cell, DataTable, useDataTable } from '@/components/block/data-table'
import { Page } from '@/components/layout/page'
import { Button } from '@/components/ui/button'
import { CardExtendTable } from '@/components/ui/card-extend'

import { mokaApi, type MokaWorkflowDto } from '@/features/moka'
import { MokaSyncTriggerDialog } from '@/features/moka/components/moka-sync-trigger-dialog'

import { useAuth } from '@/lib/auth'
import { dateId } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/moka/')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <Page>
      <Page.Breadcrumbs />
      <Page.Column>
        <WorkflowTable />
      </Page.Column>
    </Page>
  )
}

function WorkflowTable() {
  const outletId = useAuth().outlet.oId
  const { data, isLoading } = useQuery(
    mokaApi.workflowList.query({
      params: { outletId },
    })
  )
  const columns = useMemo(getColumns, [])
  const table = useDataTable({
    columns,
    data: data?.data ?? [],
    isLoading,
  })

  return (
    <CardExtendTable
      title='Moka Workflow'
      action={
        <>
          <MokaSyncTriggerDialog.Root />
          <Button size='sm' variant='outline' onClick={() => MokaSyncTriggerDialog.upsert()}>
            <DatabaseZapIcon />
            Sinkronkan
          </Button>
        </>
      }
    >
      <DataTable.Root table={table}>
        <DataTable.Table />
        <DataTable.Pagination />
      </DataTable.Root>
    </CardExtendTable>
  )
}

function getColumns(): ColumnDef<MokaWorkflowDto>[] {
  return [
    {
      accessorKey: 'meta.tag',
      header: 'Tag',
      cell: ({ row }) => row.original.meta.tag,
    },
    {
      accessorKey: 'meta.date',
      header: 'Tanggal',
      cell: ({ row }) => {
        if (row.original.meta.tag === 'sales') {
          const { from, to } = row.original.meta.date
          if (from && to) return `${dateId(from)} - ${dateId(to)}`
          else if (from) return dateId(from)
        } else {
          return '-'
        }
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => row.original.status,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => <Cell.Date date={row.original.createdAt} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      cell: ({ row }) => <Cell.Date date={row.original.updatedAt} />,
    },
  ]
}
